import React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    CircularProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

import { formatDate, formatEuro } from './evaluationFormatters';

function DetailTable({ rows }) {
    return (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, mb: 2 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Datum</TableCell>
                        <TableCell>Beleg</TableCell>
                        <TableCell>Partner</TableCell>
                        <TableCell>Beschreibung</TableCell>
                        <TableCell>Steuer</TableCell>
                        <TableCell align="right">Netto</TableCell>
                        <TableCell align="right">USt</TableCell>
                        <TableCell align="right">Brutto</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={`${row.type}-${row.reference}-${row.date}`}>
                            <TableCell>{formatDate(row.date)}</TableCell>
                            <TableCell>{row.reference || '-'}</TableCell>
                            <TableCell>{row.partner || '-'}</TableCell>
                            <TableCell>{row.description || row.category || '-'}</TableCell>
                            <TableCell>{row.taxRateLabel || '-'}</TableCell>
                            <TableCell align="right">{formatEuro(row.net)}</TableCell>
                            <TableCell align="right">{formatEuro(row.vat)}</TableCell>
                            <TableCell align="right">{formatEuro(row.gross)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default function VatDetailsAccordion({
    open,
    loading,
    error,
    details,
    onToggle,
}) {
    const invoiceEntries = details?.invoiceEntries || [];
    const incomeEntries = details?.incomeEntries || [];
    const expenseGroups = details?.expenseGroups || [];

    return (
        <Accordion expanded={open} onChange={(_, expanded) => onToggle(expanded)} sx={{ mt: 3, borderRadius: '12px !important' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack spacing={0.5}>
                    <Typography variant="subtitle1" fontWeight={700}>
                        Einzelaufstellung
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Wird bei Bedarf nachgeladen und nach Rechnungen, manuellen Einnahmen und Ausgabenkategorien gegliedert angezeigt.
                    </Typography>
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                {loading && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
                        <CircularProgress size={18} />
                        <Typography>Lade Einzelaufstellung...</Typography>
                    </Stack>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {!loading && !error && (
                    <>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Eigene Rechnungen
                        </Typography>
                        {invoiceEntries.length > 0 ? (
                            <DetailTable rows={invoiceEntries} />
                        ) : (
                            <Typography color="text.secondary" sx={{ mb: 2 }}>
                                Keine Rechnungen im gewählten Zeitraum.
                            </Typography>
                        )}

                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Manuelle Einnahmen
                        </Typography>
                        {incomeEntries.length > 0 ? (
                            <DetailTable rows={incomeEntries} />
                        ) : (
                            <Typography color="text.secondary" sx={{ mb: 2 }}>
                                Keine manuellen Einnahmen im gewählten Zeitraum.
                            </Typography>
                        )}

                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Ausgaben
                        </Typography>
                        {expenseGroups.length > 0 ? (
                            expenseGroups.map((group) => (
                                <div key={group.category || 'Sonstige'}>
                                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, mt: 2 }}>
                                        {group.category || 'Sonstige'}
                                    </Typography>
                                    <DetailTable rows={group.entries || []} />
                                </div>
                            ))
                        ) : (
                            <Typography color="text.secondary">
                                Keine Ausgaben im gewählten Zeitraum.
                            </Typography>
                        )}
                    </>
                )}
            </AccordionDetails>
        </Accordion>
    );
}
